import { useEffect } from "react";

import { useRegistryDialogs } from "../Dialogs";
import { useUnifiedRegistry } from "../unified-registry-context";
import { RegistryItem } from "../types";

import { Entity, EntityType } from "@/queries";
import { useRbac } from "@/providers/RbacProvider";
import { isModifierKeyPressed } from "@/utils/shortcuts";

export const useSelectionKeyboardShortcuts = (
  allItems: RegistryItem[],
  allowedTypes?: EntityType[],
) => {
  const registry = useUnifiedRegistry();
  const { deleteBatch } = useRegistryDialogs();
  const { isDeleteDisabled } = useRbac();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      if (event.key === "Escape") {
        registry.clearSelection();
        return;
      }

      if (isModifierKeyPressed(event) && event.key === "a") {
        event.preventDefault();

        const itemsToSelect = allowedTypes
          ? (allItems as Entity[]).filter(item => allowedTypes.includes(item.type))
          : (allItems as Entity[]);

        registry.selectEntities(itemsToSelect);
        return;
      }

      if (isModifierKeyPressed(event) && event.key === "d") {
        event.preventDefault();
        registry.clearSelection();
        return;
      }

      if (isModifierKeyPressed(event) && event.key === "Backspace") {
        if (registry.selectionDisabled || registry.selectedCount === 0) {
          return;
        }

        const selected = registry.selectedEntities;
        if (selected.some(entity => isDeleteDisabled(entity.type))) {
          return;
        }

        event.preventDefault();
        deleteBatch(selected, () => {
          registry.clearSelection();
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [registry, allItems, allowedTypes, deleteBatch, isDeleteDisabled]);
};
